import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "./useRole";

interface UserProfile {
  id: string;
  nickname: string;
  role: string;
  image_url?: string;
  preferred_game?: string;
}

// Sistema de singleton para evitar múltiplas subscriptions
class UserProfileManager {
  private static instance: UserProfileManager;
  private channels: Map<string, any> = new Map();
  private listeners: Map<string, Set<() => void>> = new Map();

  static getInstance(): UserProfileManager {
    if (!UserProfileManager.instance) {
      UserProfileManager.instance = new UserProfileManager();
    }
    return UserProfileManager.instance;
  }

  addListener(userId: string, callback: () => void): () => void {
    // Adicionar callback à lista de listeners
    if (!this.listeners.has(userId)) {
      this.listeners.set(userId, new Set());
    }
    this.listeners.get(userId)!.add(callback);

    // Criar channel apenas se não existir
    if (!this.channels.has(userId)) {
      this.createChannel(userId);
    }

    // Retornar função de cleanup
    return () => {
      const userListeners = this.listeners.get(userId);
      if (userListeners) {
        userListeners.delete(callback);
        
        // Se não há mais listeners, remover o channel
        if (userListeners.size === 0) {
          this.removeChannel(userId);
        }
      }
    };
  }

  private createChannel(userId: string) {
    console.log('👂 [UserProfileManager] Criando canal unificado para usuário:', userId);
    
    const channelName = `unified_profile_updates_${userId}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`
        },
        (payload) => {
          console.log('🔔 [UserProfileManager] Atualização detectada no perfil:', payload);
          this.notifyListeners(userId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'player_social_media',
          filter: `profile_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔔 [UserProfileManager] Atualização detectada na imagem do jogador:', payload);
          this.notifyListeners(userId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mentor_info',
          filter: `profile_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔔 [UserProfileManager] Atualização detectada na imagem do mentor:', payload);
          this.notifyListeners(userId);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ [UserProfileManager] Canal unificado conectado com sucesso');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ [UserProfileManager] Erro no canal de subscriptions');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏰ [UserProfileManager] Timeout na subscription');
        }
      });

    this.channels.set(userId, channel);
  }

  private removeChannel(userId: string) {
    const channel = this.channels.get(userId);
    if (channel) {
      console.log('🔇 [UserProfileManager] Removendo canal para usuário:', userId);
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
      this.channels.delete(userId);
      this.listeners.delete(userId);
    }
  }

  private notifyListeners(userId: string) {
    const userListeners = this.listeners.get(userId);
    if (userListeners) {
      userListeners.forEach(callback => callback());
    }
  }
}

export function useUserProfile() {
  const { authUser, role, loading: roleLoading } = useRole();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Usar ref para a função fetchUserProfile para evitar dependências no useEffect
  const fetchUserProfileRef = useRef<() => Promise<void>>();

  const fetchUserProfile = useCallback(async () => {
    if (!authUser || roleLoading) {
      console.log('⚠️ [useUserProfile] Aguardando auth ou role carregar...');
      return;
    }

    console.log('🔄 [useUserProfile] Carregando perfil do usuário:', authUser.id);

    try {
      setLoading(true);

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, nickname, role, preferred_game")
        .eq("id", authUser.id)
        .maybeSingle();

      if (profileError || !profileData) {
        console.error("❌ [useUserProfile] Erro ao buscar perfil:", profileError);
        setProfile(null);
        setLoading(false);
        return;
      }

      console.log('✅ [useUserProfile] Dados básicos do perfil carregados:', profileData);
      
      // Debug específico do nickname
      console.log('📝 [useUserProfile] NICKNAME DEBUG:');
      console.log('📝 [useUserProfile] Nickname raw:', JSON.stringify(profileData.nickname));
      console.log('📝 [useUserProfile] Nickname type:', typeof profileData.nickname);
      console.log('📝 [useUserProfile] Nickname length:', profileData.nickname?.length || 0);
      console.log('📝 [useUserProfile] Nickname truthy:', !!profileData.nickname);

      // Auto-correção do nickname se estiver vazio
      let correctedNickname = profileData.nickname;
      if (!correctedNickname || correctedNickname.trim() === '') {
        console.warn('🔧 [useUserProfile] Nickname vazio detectado! Aplicando auto-correção...');
        
        // Verificar o email para sugerir um nickname
        const emailPrefix = authUser.email?.split('@')[0] || 'Usuario';
        const suggestedNickname = emailPrefix === 'lucas.deploy.teste' ? 'Deployjogador' : emailPrefix;
        
        try {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ nickname: suggestedNickname })
            .eq('id', authUser.id);

          if (updateError) {
            console.error('❌ [useUserProfile] Erro ao auto-corrigir nickname:', updateError);
          } else {
            console.log('✅ [useUserProfile] Nickname auto-corrigido para:', suggestedNickname);
            correctedNickname = suggestedNickname;
          }
        } catch (autoCorrectError) {
          console.error('❌ [useUserProfile] Erro na auto-correção:', autoCorrectError);
        }
      }

      let imageUrl = null;

      // Se for mentor, buscar imagem da tabela mentor_info
      if (profileData.role === "mentor") {
        console.log('🔍 [useUserProfile] Buscando imagem do mentor...');
        const { data: mentorData, error: mentorError } = await supabase
          .from("mentor_info")
          .select("image_url")
          .eq("profile_id", authUser.id)
          .maybeSingle();

        if (mentorError) {
          console.log('⚠️ [useUserProfile] Erro ao buscar imagem do mentor:', mentorError.message);
        } else {
          imageUrl = mentorData?.image_url;
          console.log('📸 [useUserProfile] Imagem do mentor encontrada:', imageUrl);
        }
      }
      // Se for jogador, buscar imagem da tabela player_social_media
      else if (profileData.role === "jogador") {
        console.log('🔍 [useUserProfile] Buscando imagem do jogador...');
        
        // Usar a mesma lógica do playerProfileService para lidar com múltiplos registros
        const { data: playerDataArray, error: playerError } = await supabase
          .from("player_social_media")
          .select("image_url")
          .eq("profile_id", authUser.id);

        if (playerError) {
          console.log('⚠️ [useUserProfile] Erro ao buscar imagem do jogador:', playerError.message);
        } else if (playerDataArray && playerDataArray.length > 0) {
          // Pegar a imagem do primeiro registro se houver múltiplos
          imageUrl = playerDataArray[0]?.image_url;
          console.log('📸 [useUserProfile] Imagem do jogador encontrada:', imageUrl);
          
          if (playerDataArray.length > 1) {
            console.log('⚠️ [useUserProfile] Múltiplos registros encontrados para social media');
          }
        } else {
          console.log('📸 [useUserProfile] Nenhuma imagem encontrada para o jogador');
        }
      }

      // Lógica do nickname com logs detalhados
      let finalNickname = correctedNickname;
      if (!finalNickname || finalNickname.trim() === '') {
        console.warn('⚠️ [useUserProfile] NICKNAME AINDA VAZIO! Usando fallback "Usuário"');
        finalNickname = "Usuário";
      } else {
        console.log('✅ [useUserProfile] Nickname válido encontrado:', finalNickname);
      }

      const finalProfile = {
        id: profileData.id,
        nickname: finalNickname,
        role: profileData.role,
        image_url: imageUrl,
        preferred_game: profileData.preferred_game,
      };
      
      console.log('✅ [useUserProfile] Perfil final carregado:', finalProfile);
      console.log('🎯 [useUserProfile] NICKNAME FINAL QUE APARECERÁ NA NAVBAR:', finalProfile.nickname);
      
      setProfile(finalProfile);
      setLoading(false);
    } catch (error) {
      console.error("❌ [useUserProfile] Erro geral ao buscar perfil:", error);
      setProfile(null);
      setLoading(false);
    }
  }, [authUser, roleLoading]);

  // Atualizar a ref sempre que a função for recriada
  useEffect(() => {
    fetchUserProfileRef.current = fetchUserProfile;
  }, [fetchUserProfile]);

  // Executar apenas uma vez quando authUser e role estiverem prontos
  useEffect(() => {
    if (!authUser || roleLoading) return;
    
    fetchUserProfile();
  }, [authUser?.id, roleLoading]); // Dependências específicas para evitar loops

  // Usar o UserProfileManager para evitar múltiplas subscriptions
  useEffect(() => {
    if (!authUser) return;

    const manager = UserProfileManager.getInstance();
    const cleanup = manager.addListener(authUser.id, () => {
      if (fetchUserProfileRef.current) {
        fetchUserProfileRef.current();
      }
    });

    return cleanup;
  }, [authUser?.id]); // Usar authUser?.id para evitar loops

  // Função para forçar recarregamento (pode ser usada externamente)
  const refreshProfile = useCallback(() => {
    console.log('🔄 [useUserProfile] Forçando atualização do perfil...');
    if (fetchUserProfileRef.current) {
      fetchUserProfileRef.current();
    }
  }, []);

  return { profile, loading, refreshProfile };
} 